import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, MessageSquare, RotateCcw, Download, CreditCard, Loader2 } from "lucide-react";
import { usePortal } from "../PortalContext";
import api from "@/services/api";
import type { Application } from "../types";
import { useBranding } from "@/context/BrandingContext";
import { getInvoiceByApplication } from "@/services/paymentService";
import type { InvoiceByApplicationResponse, PaymentOption } from "@/services/paymentService";
import { getPaymentOptions, isBankTransferOption } from "@/services/paymentService";
import { PaymentMethodSelector } from "@/components/payment";
import {
    getCachedApplicationDetail,
    setCachedApplicationDetail,
    invalidateCachedApplicationDetail,
    type ApplicationDetailSnapshot,
} from "@/services/applicationDetailCache";

function formatUpdatedAt(value: string | undefined): string {
    if (!value) return "";
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatSyncedAt(value: string | null | undefined): string {
    if (!value) return "Never";
    const d = new Date(value);
    return isNaN(d.getTime())
        ? value
        : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Merges a cached/fetched detail snapshot onto a base Application record. */
function applyDetailSnapshot(base: Application, snapshot: ApplicationDetailSnapshot): Application {
    return {
        ...base,
        Application_Status: snapshot.applicationStatus || base.Application_Status,
        Stage: snapshot.stage || base.Stage,
        offer_letter_url: snapshot.offerLetterUrl ?? base.offer_letter_url,
        show_offer_letter_on_portal: snapshot.showOfferLetterOnPortal,
        acceptance_letter_url: snapshot.acceptanceLetterUrl ?? base.acceptance_letter_url,
        show_acceptance_letter_on_portal: snapshot.showAcceptanceLetterOnPortal,
        synced_at: snapshot.syncedAt ?? base.synced_at,
    };
}

export default function ApplicationView() {
    const { branding } = useBranding();
    const navigate = useNavigate();
    const { applicationNumber } = useParams<{ applicationNumber: string }>();
    const { applications, handleComplete } = usePortal();

    const baseApplication = applications.find((a: Application) => a.Application_Number === applicationNumber);
    const [application, setApplication] = useState<Application | undefined>(baseApplication);
    const [isLoading, setIsLoading] = useState(true);
    const [isSpinnerFadingOut, setIsSpinnerFadingOut] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [invoice, setInvoice] = useState<InvoiceByApplicationResponse | null>(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
    const [paymentOptionsLoading, setPaymentOptionsLoading] = useState(false);
    const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption | null>(null);
    const [showCongrats, setShowCongrats] = useState(false);
    const [congratsVisible, setCongratsVisible] = useState(false);

    const location = useLocation();

    useEffect(() => {
        const state = location.state as { newApplication?: boolean } | null;
        if (!state?.newApplication) return;
        setShowCongrats(true);
        // Trigger fade-in on next frame
        const fadeIn = requestAnimationFrame(() => setCongratsVisible(true));
        // Clear the state so refreshing doesn't replay the animation
        window.history.replaceState({ ...(window.history.state ?? {}), usr: null }, "");
        // Fire confetti burst — from both sides on wide screens, top-center on narrow
        const end = Date.now() + 2500;
        const isMobile = window.innerWidth < 640;
        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

        let confettiFrameId: number | null = null;
        const frame = () => {
            if (!prefersReducedMotion) {
                if (isMobile) {
                    confetti({ particleCount: 6, angle: 270, spread: 80, origin: { x: 0.5, y: 0 }, startVelocity: 30 });
                } else {
                    confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 } });
                    confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 } });
                }
            }
            if (Date.now() < end) confettiFrameId = requestAnimationFrame(frame);
        };
        frame();
        // Start fade-out 700ms before unmounting
        const fadeOut = setTimeout(() => setCongratsVisible(false), 4300);
        const remove = setTimeout(() => setShowCongrats(false), 5000);
        return () => {
            cancelAnimationFrame(fadeIn);
            if (confettiFrameId !== null) cancelAnimationFrame(confettiFrameId);
            clearTimeout(fadeOut);
            clearTimeout(remove);
        };
    }, [location]);

    const isDraft = application?.Action === "Complete";
    const onBack = () => navigate("/portal/applications");

    useEffect(() => {
        if (!applicationNumber) return;

        let cancelled = false;

        // If this application was fetched (by an individual or bulk sync) within
        // the last 30 seconds, skip the Salesforce callout entirely and serve
        // from the module-level cache. This prevents N individual SF callouts
        // when the user navigates back from the list, which already pre-warmed
        // the cache via the background bulk-detail fetch.
        const cachedSnapshot = getCachedApplicationDetail(applicationNumber);
        if (cachedSnapshot && baseApplication) {
            setApplication(applyDetailSnapshot(baseApplication, cachedSnapshot));
            setIsSpinnerFadingOut(true);
            const cacheTimer = setTimeout(() => { if (!cancelled) setIsLoading(false); }, 150);
            return () => {
                cancelled = true;
                clearTimeout(cacheTimer);
            };
        }

        let minLoadingComplete = false;

        const minLoadingTimer = setTimeout(() => {
            minLoadingComplete = true;
        }, 500);

        const fetchDetail = async () => {
            setIsSyncing(true);
            try {
                const response = await api.get(
                    `/integrations/hub/applications/${applicationNumber}/detail/`
                );
                if (cancelled) return;
                const data = response.data;
                if (data.status === "success") {
                    const snapshot: ApplicationDetailSnapshot = {
                        applicationStatus: data.application_status || "",
                        stage: data.stage || "",
                        offerLetterUrl: data.offer_letter_url ?? null,
                        showOfferLetterOnPortal: String(data.show_offer_letter_on_portal) === "true",
                        acceptanceLetterUrl: data.acceptance_letter_url ?? null,
                        showAcceptanceLetterOnPortal: String(data.show_acceptance_letter_on_portal) === "true",
                        syncedAt: data.synced_at ?? null,
                    };
                    setCachedApplicationDetail(applicationNumber, snapshot);
                    setApplication((prev: Application | undefined) => {
                        const base = prev ?? baseApplication;
                        if (!base) return prev;
                        return applyDetailSnapshot(base, snapshot);
                    });
                }
            } catch (error) {
                console.error("Failed to fetch application detail:", error);
            } finally {
                if (!cancelled) {
                    setIsSyncing(false);
                    // Wait for minimum loading time before fading out spinner
                    const waitForMinLoading = () => {
                        if (cancelled) return;

                        if (minLoadingComplete) {
                            setIsSpinnerFadingOut(true);
                            setTimeout(() => {
                                if (!cancelled) setIsLoading(false);
                            }, 300);
                        } else {
                            setTimeout(waitForMinLoading, 50);
                        }
                    };
                    waitForMinLoading();
                }
            }
        };

        fetchDetail();
        return () => {
            cancelled = true;
            clearTimeout(minLoadingTimer);
        };
    }, [applicationNumber]);

    useEffect(() => {
        if (baseApplication) {
            setApplication((prev: Application | undefined) => prev ? { ...baseApplication, offer_letter_url: prev.offer_letter_url, synced_at: prev.synced_at } : baseApplication);
        }
    }, [baseApplication]);

    // Fetch invoice and payment options when the application is at Payment stage
    // Payment options are filtered based on invoice criteria, so we load invoice first
    useEffect(() => {
        if (!applicationNumber || application?.Stage !== "Payment") {
            setInvoice(null);
            setPaymentOptions([]);
            return;
        }

        let cancelled = false;

        const loadPaymentData = async () => {
            // Step 1: Load invoice first
            setInvoiceLoading(true);
            try {
                const invoiceData = await getInvoiceByApplication(applicationNumber);
                if (cancelled) return;
                setInvoice(invoiceData);
            } catch (error) {
                if (cancelled) return;
                setInvoice(null);
                console.error("Failed to load invoice:", error);
            } finally {
                if (!cancelled) setInvoiceLoading(false);
            }

            // Step 2: Load payment options (filtered by invoice criteria)
            setPaymentOptionsLoading(true);
            try {
                const optionsData = await getPaymentOptions(applicationNumber);
                if (cancelled) return;
                setPaymentOptions(optionsData);
            } catch (error) {
                if (cancelled) return;
                setPaymentOptions([]);
                console.error("Failed to load payment options:", error);
            } finally {
                if (!cancelled) setPaymentOptionsLoading(false);
            }
        };

        loadPaymentData();
        return () => { cancelled = true; };
    }, [applicationNumber, application?.Stage]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
                <div
                    className="transition-opacity duration-300 ease-in-out"
                    style={{ opacity: isSpinnerFadingOut ? 0 : 1 }}
                >
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                </div>
            </div>
        );
    }

    if (!application) {
        return (
            <div className="flex flex-col items-center gap-4 py-20 text-slate-400">
                <FileText className="w-10 h-10" />
                <p className="text-sm font-bold">Application not found.</p>
                <Button variant="outline" size="sm" onClick={onBack}>Back to Applications</Button>
            </div>
        );
    }

    const normalize = (value: string | undefined | null) =>
        (value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

    const rawApplicationStatus = (application.Application_Status || "").trim();
    const hasApplicationStatus = rawApplicationStatus.length > 0;
    const applicationStatusDisplay = rawApplicationStatus.replace(/_/g, " ");

    const fields = [
        { label: "Full Name", value: application.summary?.fullName || application.Applicant_Name },
        { label: "Email", value: application.summary?.email },
        { label: "Mobile", value: application.summary?.mobile },
        { label: "Citizenship", value: application.summary?.citizenship },
        { label: "Applied Program", value: application.summary?.appliedProgram || application.Applied_Program_Name },
        { label: "Applied Term", value: application.summary?.appliedTerm || application.Applied_Term },
        { label: "Degree", value: application.summary?.degree },
        { label: "Application Stage", value: application.Stage || "—" },
        ...(hasApplicationStatus ? [{ label: "Application Status", value: applicationStatusDisplay }] : []),
        { label: "Last Updated", value: formatUpdatedAt(application.Updated_At) },
    ];

    const statusColor =
        application.Action === "Complete"
            ? "border-amber-100 text-amber-600 bg-amber-50/50"
            : application.Stage === "Evaluation"
              ? "border-blue-100 text-blue-600 bg-blue-50/50"
              : "border-emerald-100 text-emerald-600 bg-emerald-50/50";

    return (
        <div className="space-y-6">
            {/* Congratulations banner */}
            {showCongrats && (
                <div
                    className="rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 px-4 py-4 flex items-start sm:items-center gap-3 shadow-sm transition-all duration-700 ease-in-out"
                    style={{ opacity: congratsVisible ? 1 : 0, transform: congratsVisible ? "translateY(0)" : "translateY(-8px)" }}
                >
                    <span className="text-3xl leading-none shrink-0" aria-hidden="true">🎉</span>
                    <div className="min-w-0">
                        <p className="font-bold text-emerald-800 text-sm leading-snug">Congratulations! Your application has been submitted.</p>
                        <p className="text-emerald-600 text-xs mt-1 leading-snug">We'll keep you updated on its progress. Check back here anytime to track your status.</p>
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="flex items-center gap-4 px-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 hover:text-primary font-bold px-2 -ml-2"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Applications
                </Button>
            </div>

            {/* Title row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">
                            Application Details
                        </h3>
                        <p className="text-xs text-slate-400 font-medium">
                            {application.Application_Number}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {isDraft && (
                        <Button
                            size="sm"
                            onClick={() => handleComplete(application)}
                            className="flex items-center gap-2 font-black text-xs bg-primary hover:bg-primary/90 text-white rounded-xl px-4 h-9"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Resume Application
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/portal/applications/${application.Application_Number}/messages`)}
                        className="flex items-center gap-2 font-black text-xs border-slate-200 text-slate-600 hover:text-primary rounded-xl px-4 h-9"
                    >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Messages
                    </Button>
                    <Badge
                        variant="outline"
                        className={`text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest border whitespace-nowrap ${statusColor}`}
                    >
                        {application.Stage || "—"}
                    </Badge>
                </div>
            </div>
             {/* Stage timeline card */}
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Application Stage
                </h4>
                {(() => {
                    const configuredStages = (branding?.branding?.stages ?? []) as Array<{
                        label?: string;
                        value?: string;
                        key?: string;
                    }>;

                    const defaultSteps = [
                        { value: "Signed Up", label: "Signed Up" },
                        { value: "Evaluation", label: "Evaluation" },
                        { value: "Offer Letter", label: "Offer Letter" },
                        { value: "Payment", label: "Payment" },
                        { value: "Acceptance Letter", label: "Acceptance Letter" },
                        { value: "Registration", label: "Registration" },
                        { value: "Registered", label: "Registered" },
                    ];

                    const steps = configuredStages.length > 0
                        ? configuredStages.map((stage, index) => ({
                            value: String(stage.value ?? stage.key ?? index + 1),
                            label: String(stage.label ?? stage.value ?? stage.key ?? `Stage ${index + 1}`),
                        }))
                        : defaultSteps;

                    const currentStageRaw = application.Stage || "";
                    const normalizedCurrentStage = normalize(currentStageRaw);

                    let currentIndex = steps.findIndex((step) => {
                        const normalizedLabel = normalize(step.label);
                        const normalizedValue = normalize(step.value);
                        return normalizedCurrentStage === normalizedLabel || normalizedCurrentStage === normalizedValue;
                    });

                    if (currentIndex < 0) {
                        currentIndex = 0;
                    }

                    return (
                        <div className="flex items-center gap-3 flex-wrap">
                            {steps.map((step, i) => {
                                const isActive = i === currentIndex;
                                const isDone = i < currentIndex;
                                return (
                                    <div key={step.value} className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border-2 ${
                                                    isActive || isDone
                                                        ? "border-emerald-500 bg-emerald-500 text-white"
                                                        : "border-slate-200 bg-white text-slate-400"
                                                }`}
                                            >
                                                {i + 1}
                                            </div>
                                            <span
                                                className={`text-xs font-bold ${
                                                    isActive || isDone
                                                        ? "text-emerald-600"
                                                        : "text-slate-400"
                                                }`}
                                            >
                                                {step.label}
                                            </span>
                                        </div>
                                        {i < steps.length - 1 && (
                                            <div
                                                className={`w-8 h-0.5 ${
                                                    i < currentIndex ? "bg-emerald-400" : "bg-slate-200"
                                                }`}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
            </div>
            {/* Details card */}
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Application Summary
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {fields.map((item) => (
                        <div key={item.label} className="space-y-1">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                {item.label}
                            </p>
                            <p className="text-sm font-bold text-slate-800">
                                {item.value || "—"}
                            </p>
                        </div>
                    ))}
                </div>

                {application.show_offer_letter_on_portal === true &&
                    !!application.offer_letter_url && (
                    <a
                        href={application.offer_letter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 w-full rounded-xl border border-blue-100 bg-blue-50/40 hover:bg-blue-50/70 px-4 py-3 transition-colors group"
                    >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                            <Download className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-black text-slate-800">Download Offer Letter</p>
                            <p className="text-[10px] text-slate-400 font-medium">Click to open PDF</p>
                        </div>
                    </a>
                )}

                {application.show_acceptance_letter_on_portal === true &&
                    !!application.acceptance_letter_url && (
                    <a
                        href={application.acceptance_letter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 w-full rounded-xl border border-emerald-100 bg-emerald-50/40 hover:bg-emerald-50/70 px-4 py-3 transition-colors group"
                    >
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                            <Download className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-black text-slate-800">Download Acceptance Letter</p>
                            <p className="text-[10px] text-slate-400 font-medium">Click to open PDF</p>
                        </div>
                    </a>
                )}
            </div>


            {/* Payment section — only shown when application is at Payment stage */}
            {application.Stage === "Payment" && (
                <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Payment
                    </h4>

                    {/* Loading state — show while invoice or payment options are loading */}
                    {(invoiceLoading || paymentOptionsLoading) && (
                        <div className="flex items-center gap-3 text-slate-400">
                            <CreditCard className="w-5 h-5 animate-pulse" />
                            <p className="text-sm font-medium">Loading payment information…</p>
                        </div>
                    )}

                    {/* No invoice found */}
                    {!invoiceLoading && !invoice && (
                        <p className="text-sm text-slate-500 font-medium">
                            No invoice has been issued for this application yet. Please check back later.
                        </p>
                    )}

                    {/* Invoice loaded — show invoice details and payment options */}
                    {!invoiceLoading && !paymentOptionsLoading && invoice && (
                        <div className="space-y-5">
                            {/* Invoice summary — only shown when payment action is available */}
                            {invoice.status !== "paid" && invoice.status !== "receipt_uploaded" && (
                            <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Invoice</p>
                                    <p className="text-sm font-bold text-slate-800">{invoice.invoice_number || `#${invoice.id}`}</p>
                                    {invoice.description && (
                                        <p className="text-xs text-slate-500">{invoice.description}</p>
                                    )}
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-2xl font-black text-slate-900 tracking-tight">
                                        {parseFloat(invoice.amount).toLocaleString("tr-TR", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                        <span className="text-sm font-bold text-slate-500 ml-1">{invoice.currency_code}</span>
                                    </p>
                                    <Badge
                                        variant="outline"
                                        className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest mt-1 ${
                                            invoice.status === "rejected"
                                                ? "border-red-100 text-red-600 bg-red-50"
                                                : "border-amber-100 text-amber-600 bg-amber-50"
                                        }`}
                                    >
                                        {invoice.status === "rejected"
                                            ? "Rejected"
                                            : invoice.status === "partially_paid"
                                            ? "Partially Paid"
                                            : invoice.status === "overdue"
                                            ? "Overdue"
                                            : invoice.status === "refunded"
                                            ? "Refunded"
                                            : invoice.status === "cancelled"
                                            ? "Cancelled"
                                            : "Pending"}
                                    </Badge>
                                </div>
                            </div>
                            )}

                            {/* If invoice is paid, show confirmation */}
                            {invoice.status === "paid" && (
                                <p className="text-sm font-bold text-emerald-600">
                                    ✓ This invoice has been paid. Thank you!
                                </p>
                            )}

                            {/* Receipt submitted — awaiting admin review */}
                            {invoice.status === "receipt_uploaded" && (
                                <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-700 font-medium">
                                    Your deposit receipt has been submitted and is awaiting review. We will notify you once it is processed.
                                </div>
                            )}

                            {/* Receipt rejected — show rejection reason */}
                            {invoice.status === "rejected" && (() => {
                                const rejectionMessage = invoice.rejection_reason === "Other"
                                    ? invoice.status_message
                                    : invoice.rejection_reason;
                                return (
                                    <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-sm text-red-700">
                                        <p className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                            <span className="font-semibold shrink-0">Your receipt was rejected for the following reason:</span>
                                            {rejectionMessage && <span className="font-bold text-base">{rejectionMessage}</span>}
                                        </p>
                                        <p className="mt-2 text-xs text-red-400">Please upload a new receipt below.</p>
                                    </div>
                                );
                            })()}

                            {/* Payment options — show for unpaid/rejected invoices */}
                            {invoice.status !== "paid" && invoice.status !== "receipt_uploaded" && paymentOptions.length > 0 && (
                                <>
                                    <div className="space-y-2">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                            Select Payment Method
                                        </p>
                                        <div className="flex flex-wrap gap-3">
                                            {paymentOptions.map((option) => {
                                                const isSelected = selectedPaymentOption?.id === option.id;
                                                return (
                                                    <Button
                                                        key={option.id}
                                                        variant={isSelected ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => setSelectedPaymentOption(isSelected ? null : option)}
                                                        className={`h-9 px-4 font-bold text-xs rounded-xl transition-all ${
                                                            isSelected
                                                                ? "bg-primary hover:bg-[#002b66] text-white"
                                                                : "border-slate-200 text-slate-700 hover:border-primary hover:text-primary"
                                                        }`}
                                                    >
                                                        <CreditCard className="w-3.5 h-3.5 mr-2" />
                                                        {option.name}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Bank Transfer: show file uploader */}
                                    {selectedPaymentOption && isBankTransferOption(selectedPaymentOption) && (
                                        <PaymentMethodSelector
                                            invoiceId={invoice.id}
                                            currencyCode={invoice.currency_code}
                                            availableProviders={["bank_transfer"]}
                                            bankTransferInfo={branding?.branding?.bank_transfer_info}
                                            onSuccess={() => {
                                                if (applicationNumber) {
                                                    invalidateCachedApplicationDetail(applicationNumber);
                                                    getInvoiceByApplication(applicationNumber).then(setInvoice).catch(() => null);
                                                }
                                            }}
                                        />
                                    )}

                                    {/* Online Payment: show gateway provider selector */}
                                    {selectedPaymentOption && !isBankTransferOption(selectedPaymentOption) && (() => {
                                        const onlineProviders = invoice.available_providers.filter(
                                            (provider) => provider !== "bank_transfer"
                                        );
                                        return onlineProviders.length > 0 ? (
                                            <PaymentMethodSelector
                                                invoiceId={invoice.id}
                                                currencyCode={invoice.currency_code}
                                                availableProviders={onlineProviders}
                                                onSuccess={() => {
                                                    if (applicationNumber) {
                                                        invalidateCachedApplicationDetail(applicationNumber);
                                                        getInvoiceByApplication(applicationNumber).then(setInvoice).catch(() => null);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-500 font-medium">
                                                No online payment methods are available at this time. Please contact your advisor.
                                            </p>
                                        );
                                    })()}
                                </>
                            )}

                            {/* No payment options available */}
                            {invoice.status !== "paid" && invoice.status !== "receipt_uploaded" && paymentOptions.length === 0 && (
                                <p className="text-sm text-slate-500 font-medium">
                                    No payment methods are available at this time. Please contact your advisor.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-col items-end gap-1 px-2 pb-2">
                <p className="text-[10px] text-slate-400">
                    {isSyncing
                        ? "Syncing with Salesforce…"
                        : application.synced_at
                          ? `Last update: ${formatSyncedAt(application.synced_at)}`
                          : ""}
                </p>
            </div>
        </div>
    );
}